import { Select } from '@99designs/common/components/Select';
import { SmartShootPhotographyIndoorsOrOutdoors } from '__generated__/types';
import { updatePhotographyBrief_updateStructuredBrief_fields_SmartShootPhotographyFields as PhotographyBriefFields } from '__generated__/updatePhotographyBrief';
import Checkbox from 'common/components/Checkbox';
import Text from 'common/components/Text';
import { handlePromise } from 'common/utils/Bugsnag';
import DateTimePicker from 'projects/components/DateTimePicker';
import { ObservableForm, Observe } from 'projects/components/ObservableForm';
import BriefStep from 'projects/components/SmartShoot/BriefStep';
import BriefStepField from 'projects/components/SmartShoot/BriefStepField';
import LocationSearchInput, {
    SearchResult,
} from 'projects/components/SmartShoot/LocationSearchInput';
import React from 'react';

interface Props {
    form: ObservableForm<PhotographyBriefFields>;
    step: number;
}

export default class PhotographyShootForm extends React.Component<Props> {
    render() {
        return (
            <BriefStep
                step={this.props.step}
                heading="Your photography shoot"
                subheading="Provide a location, day and time for your photo shoot."
                briefType="photo"
                rightAlignImage
            >
                <Observe
                    form={this.props.form}
                    name="address"
                    render={() => (
                        <BriefStepField
                            label="Where would you like to shoot?"
                            description="We’ll find a photographer nearby who can meet you on-location. Currently, we are only able to send photographers to locations based in the United States."
                            errorMessage={this.props.form.getError('address')}
                            required
                        >
                            <LocationSearchInput
                                placeholder="123 Hobbiton Lane, The Shire"
                                initialAddress={this.getInitialAddress()}
                                error={!!this.props.form.getError('address')}
                                name="address"
                                onChange={result => {
                                    this.setAddressFields(result);
                                }}
                            />
                        </BriefStepField>
                    )}
                />
                <Observe
                    form={this.props.form}
                    name="locationName"
                    render={() => (
                        <BriefStepField
                            label="Description of location"
                            errorMessage={this.props.form.getError('locationName')}
                        >
                            <Text
                                placeholder="eg. Bilbo’s Bakery at The Shire"
                                maxLength={500}
                                fill
                                {...this.props.form.attach('locationName')}
                            />
                        </BriefStepField>
                    )}
                />
                <Observe
                    form={this.props.form}
                    name="datetime"
                    render={() => (
                        <BriefStepField
                            label="When would you like to shoot?"
                            description="Select a date that works for you, and your photographer will confirm that it works for them."
                            errorMessage={this.props.form.getError('datetime')}
                            variations={['no-margin']}
                            required
                        >
                            <DateTimePicker
                                name="datetime"
                                value={this.props.form.get('datetime')}
                                onChange={date => date && this.props.form.set('datetime', date)}
                                disabled={this.props.form.get('deferred')}
                                error={!!this.props.form.getError('datetime')}
                            />
                        </BriefStepField>
                    )}
                />
                <p className="paragraph paragraph--spacious-bottom">
                    <Observe
                        form={this.props.form}
                        name="deferred"
                        render={() => {
                            return (
                                <Checkbox
                                    name="deferred"
                                    checked={this.props.form.get('deferred')}
                                    onChange={({ checked }) => {
                                        handlePromise(
                                            this.props.form.set('deferred', checked),
                                            'SmartShoot brief deferred'
                                        );
                                    }}
                                >
                                    Not sure (You can schedule this later)
                                </Checkbox>
                            );
                        }}
                    />
                </p>
                <Observe
                    form={this.props.form}
                    name="indoorsOrOutdoors"
                    render={() => (
                        <BriefStepField
                            label="Will the shoot be indoors or outdoors?"
                            description="Help your photographer plan for the shoot by letting them know what type of environment they expect."
                            errorMessage={this.props.form.getError('indoorsOrOutdoors')}
                        >
                            <Select
                                variations={['max']}
                                name="indoorsOrOutdoors"
                                options={[
                                    { label: 'Select...', value: '', disabled: true },
                                    {
                                        label: 'Indoors',
                                        value: SmartShootPhotographyIndoorsOrOutdoors.INDOORS,
                                    },
                                    {
                                        label: 'Outdoors',
                                        value: SmartShootPhotographyIndoorsOrOutdoors.OUTDOORS,
                                    },
                                    {
                                        label: 'Both',
                                        value: SmartShootPhotographyIndoorsOrOutdoors.BOTH,
                                    },
                                ]}
                                onChange={({ value }) => {
                                    handlePromise(
                                        this.props.form.set('indoorsOrOutdoors', value),
                                        'PhotographyBrief indoorsOrOutdoors'
                                    );
                                }}
                                value={this.props.form.get('indoorsOrOutdoors')}
                            />
                        </BriefStepField>
                    )}
                />
            </BriefStep>
        );
    }

    private getInitialAddress(): string {
        return [
            this.props.form.get('address'),
            this.props.form.get('city'),
            this.props.form.get('state'),
            this.props.form.get('country'),
        ]
            .filter(field => field !== '')
            .join(', ');
    }

    private setAddressFields(result: SearchResult) {
        const setFieldsOp = Promise.all([
            this.props.form.set('address', result.address),
            this.props.form.set('city', result.city),
            this.props.form.set('country', result.country),
            this.props.form.set('state', result.state),
            this.props.form.set('zipCode', result.zipCode),
            this.props.form.set('latitude', result.latitude),
            this.props.form.set('longitude', result.longitude),
        ]);
        handlePromise(setFieldsOp, 'SmartShoot brief address fields');
    }
}
